import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Paper,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import LanguageIcon from '@mui/icons-material/Language';

interface LanguageSelectorProps {
  /** Current language code */
  currentLanguage: string;
  /** Callback when language changes */
  onLanguageChange: (language: string) => void;
  /** Whether the component is in a loading state */
  loading?: boolean;
}

/**
 * LanguageSelector component allows users to select their preferred language
 * independent of browser settings. Language preference is stored in a cookie
 * and persists across browser restarts.
 */
export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  currentLanguage,
  onLanguageChange,
  loading = false,
}) => {
  const { t } = useTranslation();

  const handleLanguageChange = (event: SelectChangeEvent) => {
    onLanguageChange(event.target.value);
  };

  const languages = [
    { code: 'de', label: 'Deutsch', nativeLabel: 'Deutsch' },
    { code: 'en', label: 'English', nativeLabel: 'English' },
  ];

  return (
    <Paper
      elevation={1}
      sx={{
        p: 3,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <LanguageIcon sx={{ mr: 1.5, color: 'primary.main' }} />
        <Typography variant="h6">
          {t('settings.language.title')}
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('settings.language.description')}
      </Typography>

      <FormControl fullWidth>
        <InputLabel id="language-selector-label">
          {t('settings.language.selectLanguage')}
        </InputLabel>
        <Select
          labelId="language-selector-label"
          id="language-selector"
          value={currentLanguage}
          label={t('settings.language.selectLanguage')}
          onChange={handleLanguageChange}
          disabled={loading}
        >
          {languages.map((language) => (
            <MenuItem key={language.code} value={language.code}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>{language.nativeLabel}</Typography>
                <Typography variant="caption" color="text.secondary">
                  ({language.label})
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        {t('settings.language.note')}
      </Typography>
    </Paper>
  );
};
